import { createContext, useContext, type ReactNode } from 'react';
import {
  DEFAULT_RESOLVED_CONFIG,
  useTenantConfig,
  type ResolvedTenantConfig,
} from '@hooks/useTenantConfig';

type TenantConfigContextValue = ResolvedTenantConfig & {
  /** True while the initial `/tenantConfig` fetch is in flight. */
  isLoading: boolean;
};

// Seeded with defaults so consumers rendered outside the provider (or before the
// fetch resolves / after it fails) still get the Novus baseline.
const TenantConfigContext = createContext<TenantConfigContextValue>({
  ...DEFAULT_RESOLVED_CONFIG,
  isLoading: true,
});

/**
 * Runs the tenant-config query once (must sit below QueryClientProvider and below
 * SellConfigProvider, which configures the API base URL first) and exposes the
 * resolved theme overrides, branding and features to the tree. Never blocks
 * rendering: it serves defaults until the fetch resolves, and keeps defaults if
 * the fetch fails so the app still boots.
 */
export const TenantConfigProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const { data, isPending } = useTenantConfig();
  const value: TenantConfigContextValue = { ...(data ?? DEFAULT_RESOLVED_CONFIG), isLoading: isPending };

  return <TenantConfigContext.Provider value={value}>{children}</TenantConfigContext.Provider>;
};

export const useTenantConfigContext = (): TenantConfigContextValue => useContext(TenantConfigContext);
